import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, Bot, Filter, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { promptTemplates, libraries } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Table, Pagination, Autocomplete } from '@anyemp/ui-kit';
import { PAGINATION_OPTIONS } from '../utils/paginationConstants';
import { loadColumnsStateFromLS, useTableColumns } from '../utils/tableColumnsPersistence';
import SearchInput from '../components/UI/SearchInput';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import PromptTemplateModal from '../components/PromptTemplate/PromptTemplateModal';
import Avatar from '../components/UI/Avatar';
import ActivityFitPanel from '../components/UI/ActivityFitPanel';
import SavedFiltersDropdown from '../components/SavedFilters/SavedFiltersDropdown';
import { PAGE_KEYS } from '../constants/savedFilterPageKeys';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import DataViewToggle from '../components/UI/DataViewToggle';
import PageHeader from '../components/UI/PageHeader';
import ActionGroup from '../components/UI/ActionGroup';
import { RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';

const SAVED_FILTER_KEYS = ['search', 'tool_id', 'inner_client_id', 'is_default', 'is_draft'];

const UserCell = ({ user, fallback }) => {
  if (!user) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">{fallback || '—'}</span>;
  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '—';
  return (
    <div className="flex items-center gap-1.5">
      <Avatar avatar={user.avatar} name={displayName} size="xs" />
      <span>{displayName}</span>
    </div>
  );
};

const BASE_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, alwaysPinned: true, alwaysVisible: true },
  { key: 'tool_id', label: 'Platform', sortable: false },
  { key: 'inner_client_id', label: 'Inner Client', sortable: false },
  { key: 'is_default', label: 'Default', sortable: false },
  { key: 'body', label: 'Preview', sortable: false },
  { key: 'created_by', label: 'Created by', sortable: false },
  { key: 'created_at', label: 'Created', sortable: true },
  { key: 'updated_at', label: 'Updated', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
];

const PromptTemplates = () => {
  const { hasPermission } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });
  const [filters, setFilters] = useState({
    search: '',
    tool_id: '',
    inner_client_id: '',
    is_default: '',
    is_draft: '',
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'DESC'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isActivityFitOpen, setIsActivityFitOpen] = useState(false);
  const [toolOptions, setToolOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('prompt-templates');
  const lookupsLoaded = useRef(false);

  const canCreate = hasPermission('talent-service', 'prompt_template', 'create');
  const canUpdate = hasPermission('talent-service', 'prompt_template', 'update');
  const canDelete = hasPermission('talent-service', 'prompt_template', 'delete');

  useEffect(() => {
    if (!lookupsLoaded.current) loadLookups();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [filters]);

  const loadLookups = async () => {
    try {
      const [toolsRes, clientsRes] = await Promise.all([
        libraries.getContactTools().catch(() => ({ data: [] })),
        libraries.getInnerClients().catch(() => ({ data: [] }))
      ]);
      const tools = Array.isArray(toolsRes?.data) ? toolsRes.data : (Array.isArray(toolsRes) ? toolsRes : []);
      const clients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
      setToolOptions(tools.map(t => ({ value: String(t.id), label: t.name || t.title })));
      setClientOptions(clients.map(c => ({ value: String(c.id), label: c.name })));
      lookupsLoaded.current = true;
    } catch (err) {
      console.error('Failed to load lookups:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      tool_id: '',
      inner_client_id: '',
      is_default: '',
      is_draft: '',
      page: 1,
      limit: 20,
      sort_by: 'created_at',
      sort_order: 'DESC'
    });
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      const res = await promptTemplates.getAll(params);
      const data = res?.data || [];
      const meta = res?.meta || {};
      setTemplates(Array.isArray(data) ? data : []);
      setPagination({
        current_page: meta.page || 1,
        total_pages: meta.totalPages || 1,
        total_items: meta.count || 0,
        items_per_page: filters.limit
      });
    } catch (err) {
      toast.error('Failed to load prompt templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await promptTemplates.remove(id);
      toast.success('Prompt template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSort = useCallback((key) => {
    setFilters(prev => ({
      ...prev,
      sort_by: key,
      sort_order: prev.sort_by === key && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
      page: 1
    }));
  }, []);

  const rowKey = useCallback((template) => template.id, []);

  const renderCell = (column, row) => {
    switch (column.key) {
      case 'name':
        return (
          <div className="flex items-center gap-2">
            <Link
              to={`/prompts/${row.id}`}
              className="font-medium text-light-text-primary dark:text-dark-text-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {row.name}
            </Link>
            {row.is_draft && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-700">
                Draft
              </span>
            )}
          </div>
        );
      case 'tool_id':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.tool?.name || row.tool?.title || (row.tool_id ? row.tool_id : '—')}</span>;
      case 'inner_client_id':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.inner_client?.name || (row.inner_client_id ? row.inner_client_id : '—')}</span>;
      case 'is_default':
        return row.is_default ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            Default
          </span>
        ) : null;
      case 'body':
        return (
          <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary truncate max-w-xs block">
            {row.body?.slice(0, 80)}{row.body?.length > 80 ? '...' : ''}
          </span>
        );
      case 'created_by':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary"><UserCell user={row.createdByUser} fallback={row.created_by} /></span>;
      case 'created_at':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</span>;
      case 'updated_at':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}</span>;
      default:
        return null;
    }
  };

  const renderActions = (row) => (
    <div className="flex items-center justify-end gap-2">
      {canUpdate && (
        <button
          onClick={() => { setEditingTemplate(row); setModalOpen(true); }}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-blue-600 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-blue-400"
          title="Edit"
        >
          <Edit size={16} />
        </button>
      )}
      {canDelete && (
        <button
          onClick={() => setDeleteTarget(row)}
          disabled={deletingId === row.id}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-red-600 disabled:opacity-50 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-red-400"
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );

  const columns = useMemo(
    () => BASE_COLUMNS.map((col) => ({
      ...col,
      render: col.key === 'actions'
        ? (row) => renderActions(row)
        : (row) => renderCell(col, row),
    })),
    [canUpdate, canDelete, deletingId]
  );

  const sorting = useMemo(
    () => ({ sort_by: filters.sort_by, sort_order: filters.sort_order }),
    [filters.sort_by, filters.sort_order]
  );

  const tableColumnsState = useTableColumns(columns, {
    ...loadColumnsStateFromLS('promptTemplates_columns', { defaultPinned: ['name'] }),
    onChange: (state) => {
      try { localStorage.setItem('promptTemplates_columns', JSON.stringify(state)); } catch {}
    },
  });

  const hasActiveFilters = filters.search || filters.tool_id || filters.inner_client_id || filters.is_default || filters.is_draft;

  const paginationControl = (
    <Pagination
      currentPage={pagination.current_page}
      totalPages={pagination.total_pages}
      totalItems={pagination.total_items}
      itemsPerPage={pagination.items_per_page}
      onPageChange={page => setFilters(prev => ({ ...prev, page }))}
      showResults="range"
      perPageOptions={PAGINATION_OPTIONS}
      perPage={filters.limit}
      onPerPageChange={limit => setFilters(prev => ({ ...prev, limit: parseInt(limit), page: 1 }))}
      showOnSinglePage
      hasFirstLast
      hasGoToPage
    />
  );

  return (
    <div className="min-h-full space-y-6 bg-light-bg-primary dark:bg-dark-bg-primary">
      {/* Header */}
      <PageHeader
        title={<span className="inline-flex flex-wrap items-center gap-3"><Bot className="text-primary-600" size={24} /> Prompt Templates <span className="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">({pagination.total_items})</span></span>}
        actions={<ActionGroup>
          <button
            onClick={() => setIsActivityFitOpen(true)}
            className="flex min-h-10 items-center gap-1.5 rounded-md border border-light-border-primary px-3 py-1.5 text-sm text-light-text-secondary transition-colors hover:bg-light-bg-secondary hover:text-light-text-primary dark:border-dark-border-primary dark:text-dark-text-secondary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
          >
            <Activity size={16} />
            Activity Feed
          </button>
          {canCreate && (
            <button
              onClick={() => { setEditingTemplate(null); setModalOpen(true); }}
              className="flex min-h-10 items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-700"
            >
              <Plus size={16} />
              Add Prompt
            </button>
          )}
          <DataViewToggle value={viewMode} onChange={setViewMode} />
        </ActionGroup>}
      />

      {/* Filters panel */}
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">Filters</h3>
          <div className="flex flex-wrap items-center gap-3">
            <SavedFiltersDropdown
              pageKey={PAGE_KEYS.PROMPT_TEMPLATES}
              currentFilters={filters}
              applyKeys={SAVED_FILTER_KEYS}
              onApply={(saved) => setFilters(prev => ({ ...prev, ...saved, page: 1 }))}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              <Filter size={14} />
              {showFilters ? 'Hide' : 'Show'} Filters
              {hasActiveFilters && !showFilters && (
                <span className="ml-1 w-2 h-2 rounded-full bg-primary-500 inline-block" />
              )}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Search</label>
                <SearchInput
                  value={filters.search}
                  onChange={val => handleFilterChange('search', val)}
                  placeholder="Search prompts..."
                  debounceTime={300}
                  showIcon
                />
              </div>

              {/* Platform */}
              <Autocomplete
                clearable
                fullWidth
                label="Platform"
                options={toolOptions}
                value={filters.tool_id}
                onChange={val => handleFilterChange('tool_id', val || '')}
                placeholder="Any platform..."
              />

              {/* Inner Client */}
              <Autocomplete
                clearable
                fullWidth
                label="Inner Client"
                options={clientOptions}
                value={filters.inner_client_id}
                onChange={val => handleFilterChange('inner_client_id', val || '')}
                placeholder="Any client..."
              />

              {/* Is Default */}
              <Autocomplete
                clearable
                fullWidth
                label="Default"
                options={[
                  { value: 'true', label: 'Default only' },
                  { value: 'false', label: 'Non-default' }
                ]}
                value={filters.is_default}
                onChange={val => handleFilterChange('is_default', val || '')}
                placeholder="Any..."
              />

              {/* Draft */}
              <Autocomplete
                clearable
                fullWidth
                label="Status"
                options={[
                  { value: 'false', label: 'Published' },
                  { value: 'true', label: 'Draft' }
                ]}
                value={filters.is_draft}
                onChange={val => handleFilterChange('is_draft', val || '')}
                placeholder="Any..."
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {viewMode === 'cards' && (
        <div className="space-y-4">
          {/* The prompt list API always returns created_at DESC and ignores sort params,
              so card sort controls stay hidden until the backend supports them. */}
          {loading ? <div className="py-12 text-center text-light-text-tertiary">Loading prompts…</div> : templates.length ? (
            <ResponsiveCardGrid>
              {templates.map((row) => (
                <RecordCard key={row.id}>
                  <RecordCardHeader
                    title={<Link to={`/prompts/${row.id}`} className="hover:text-primary-600">{row.name}</Link>}
                    badge={row.is_draft ? <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Draft</span> : row.is_default ? <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-300">Default</span> : null}
                    actions={renderActions(row)}
                  />
                  <RecordCardFields>
                    <RecordCardField label="Platform">{row.tool?.name || row.tool?.title || '—'}</RecordCardField>
                    <RecordCardField label="Client">{row.inner_client?.name || '—'}</RecordCardField>
                    <RecordCardField label="Created">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</RecordCardField>
                    <RecordCardField label="Updated">{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}</RecordCardField>
                    <RecordCardField label="Preview" className="sm:col-span-2">{row.body?.slice(0, 120) || '—'}</RecordCardField>
                  </RecordCardFields>
                </RecordCard>
              ))}
            </ResponsiveCardGrid>
          ) : <div className="py-12 text-center text-light-text-tertiary">No prompt templates found</div>}
          <div className="overflow-x-auto">{paginationControl}</div>
        </div>
      )}
      {viewMode === 'table' && <Table
        columns={columns}
        data={templates}
        isLoading={loading}
        rowKey={rowKey}
        sorting={sorting}
        onSort={handleSort}
        columnMenu
        {...tableColumnsState}
        emptyState={
          <div className="py-8 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
            No prompt templates found
          </div>
        }
        footer={paginationControl}
      />}

      <PromptTemplateModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTemplate(null); }}
        template={editingTemplate}
        onSaved={fetchTemplates}
      />
      <ActivityFitPanel
        isOpen={isActivityFitOpen}
        onClose={() => setIsActivityFitOpen(false)}
        entityType="PromptTemplate"
        title="Activity Feed — Prompt Templates"
      />
      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget.id)}
        title="Delete Prompt Template"
        message={<>Delete prompt template <strong>{deleteTarget?.name}</strong>? This action cannot be undone.</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deletingId === deleteTarget?.id}
      />
    </div>
  );
};

export default PromptTemplates;
